// Sunset Flame - Warm reds and oranges, fiery sunset palette
// Key colors:
//   Charcoal Ember #2D1810 - backgrounds, headers, dark UI
//   Flame Orange #FF6B35 - CTAs, highlights, action buttons
//   Sunset Red #E63946 - secondary elements, accents
//   Golden Hour #FFD166 - success states, highlights
//   Warm Parchment #FFF5E6 - card backgrounds, text areas, light base
//   Rose Ash #C9A8A0 - secondary text, disabled states, subtle borders

export const lightTheme = {
  name: 'light',

  /* === Brand === */
  primaryColor: '#FF6B35', // Flame Orange - CTAs, highlights
  primaryDark: '#E05520', // Deeper orange - hover states
  secondaryColor: '#E63946', // Sunset Red - secondary elements

  successColor: '#22c55e', // Green - availability, success
  dangerColor: '#ef4444', // Red - errors, destructive actions
  warningColor: '#FFD166', // Golden Hour - warnings
  infoColor: '#E63946', // Sunset Red - informational

  /* === Backgrounds === */
  bgPrimary: '#FFF5E6', // Warm Parchment - main background
  bgSecondary: '#FFE8CC', // Soft peach
  bgTertiary: '#FFDAB3', // Warm peach

  background: '#FFDAB3', // Page background
  cardBackground: '#ffffff', // White cards
  hoverBackground: '#FFE8CC', // Warm hover

  /* === Text === */
  textPrimary: '#2D1810', // Charcoal Ember
  textSecondary: '#5C3324', // Mid ember
  textMuted: '#8B5E4B', // Muted brown

  /* === Borders & Shadows === */
  borderColor: '#E8C4A8', // Warm border

  shadow: '0 1px 3px 0 rgba(45, 24, 16, 0.18), 0 1px 2px 0 rgba(0, 0, 0, 0.14)',
  shadowLg:
    '0 10px 25px -5px rgba(45, 24, 16, 0.35), 0 8px 10px -6px rgba(0, 0, 0, 0.35)',

  primaryHover: '#E05520', // --primary-hover
  primaryStrong: '#B83A0A', // --primary-strong (deep flame, gradient endpoint)
  accentColor: '#E63946', // --accent-color (Sunset Red)
  shadowMd:
    '0 4px 12px -2px rgba(45, 24, 16, 0.22), 0 2px 6px -1px rgba(0, 0, 0, 0.18)', // --shadow-md
  shadowXl:
    '0 20px 50px -10px rgba(45, 24, 16, 0.45), 0 12px 20px -8px rgba(0, 0, 0, 0.4)', // --shadow-xl
  shadowSoft: 'rgba(255, 107, 53, 0.12)', // --shadow-soft

  /* === Messaging / States === */
  primaryLight: 'rgba(255, 107, 53, 0.14)', // Flame Orange tint
  unreadBackground: 'rgba(255, 107, 53, 0.06)',
  messageBubbleBg: '#FFE8CC',
  messageReceivedBg: '#ffffff',
  dangerLight: 'rgba(239, 68, 68, 0.1)',
};

export const darkTheme = {
  name: 'dark',

  /* === Brand === */
  primaryColor: '#FF6B35', // Flame Orange - CTAs, highlights
  primaryDark: '#E05520', // Deeper orange - hover states
  secondaryColor: '#E63946', // Sunset Red - secondary elements

  successColor: '#22c55e', // Green - availability, success
  dangerColor: '#ef4444', // Red - errors, destructive actions
  warningColor: '#FFD166', // Golden Hour - warnings
  infoColor: '#E63946', // Sunset Red - informational

  /* === Backgrounds === */
  bgPrimary: '#1A0D08', // Deep ember
  bgSecondary: '#2D1810', // Charcoal Ember
  bgTertiary: '#4A2A1C', // Lighter ember

  background: '#1A0D08', // Page background
  cardBackground: '#2D1810', // Charcoal Ember cards
  hoverBackground: '#4A2A1C', // Hover

  /* === Text === */
  textPrimary: '#FFF5E6', // Warm Parchment
  textSecondary: '#C9A8A0', // Rose Ash
  textMuted: '#A8857A', // Muted rose

  /* === Borders & Shadows === */
  borderColor: '#5C3324', // Mid ember border

  shadow: '0 1px 3px 0 rgba(255, 107, 53, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.8)',
  shadowLg:
    '0 18px 35px -10px rgba(0, 0, 0, 0.9), 0 0 40px rgba(255, 107, 53, 0.25)',

  primaryHover: '#FF8C5A', // --primary-hover
  primaryStrong: '#FFAA7A', // --primary-strong (bright flame, gradient endpoint)
  accentColor: '#FFD166', // --accent-color (Golden Hour)
  shadowMd:
    '0 4px 12px -2px rgba(255, 107, 53, 0.3), 0 2px 6px -1px rgba(0, 0, 0, 0.6)', // --shadow-md
  shadowXl:
    '0 28px 60px -14px rgba(0, 0, 0, 0.9), 0 0 60px rgba(255, 107, 53, 0.4)', // --shadow-xl
  shadowSoft: 'rgba(255, 107, 53, 0.2)', // --shadow-soft

  /* === Messaging / States === */
  primaryLight: 'rgba(255, 107, 53, 0.3)', // Flame Orange tint
  unreadBackground: 'rgba(255, 107, 53, 0.16)',
  messageBubbleBg: '#4A2A1C',
  messageReceivedBg: '#5E3828',
  dangerLight: 'rgba(239, 68, 68, 0.25)',
};

export const themes = {
  light: lightTheme,
  dark: darkTheme,
};
